import React from "react";
import { Badge } from "@/ui-components/ui/badge";
import { Button } from "@/ui-components/ui/button";
import { Dialog, DialogContent } from "@/ui-components/ui/dialog";
import {
  CheckCircle2,
  MousePointerClick,
  PlayCircle,
  Sparkles,
} from "lucide-react";
import {
  DEFAULT_VIDEO_POSTER,
  DEFAULT_VIDEO_SRC,
  PLAN_CONFIG,
  type PlanType,
} from "./shared";

interface OnboardingVideoDialogProps {
  open?: boolean;
  plan: PlanType;
  onSkip: () => void;
  onVideoEnd?: () => void;
  onVideoError?: () => void;
  videoEnded?: boolean;
  videoError?: boolean;
  videoSrc?: string;
  videoPoster?: string;
}

export const OnboardingVideoDialog = ({
  open = true,
  plan,
  onSkip,
  onVideoEnd,
  onVideoError,
  videoEnded = false,
  videoError = false,
  videoSrc = DEFAULT_VIDEO_SRC,
  videoPoster = DEFAULT_VIDEO_POSTER,
}: OnboardingVideoDialogProps) => {
  const currentPlanConfig = PLAN_CONFIG[plan];

  return (
    <Dialog open={open} onOpenChange={() => undefined}>
      <DialogContent
        isClosable={false}
        className="left-0 top-0 flex h-[100dvh] max-h-[100dvh] w-screen max-w-none translate-x-0 translate-y-0 flex-col gap-0 rounded-none border-0 bg-white p-0 shadow-2xl sm:left-[50%] sm:top-[50%] sm:h-auto sm:w-[calc(100%-2rem)] sm:max-h-[90vh] sm:max-w-5xl sm:translate-x-[-50%] sm:translate-y-[-50%] sm:overflow-hidden sm:rounded-3xl sm:border-gray-100 sm:shadow-xl lg:w-full"
        onEscapeKeyDown={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-white sm:overflow-hidden lg:grid lg:grid-cols-[1.3fr_1fr]">
          <div className="relative flex min-h-[280px] flex-shrink-0 items-center justify-center bg-black sm:min-h-[480px]">
            <video
              className="absolute inset-0 h-full w-full object-contain"
              controls
              autoPlay
              muted
              playsInline
              poster={videoPoster}
              onEnded={onVideoEnd}
              onError={onVideoError}
            >
              {videoSrc ? <source src={videoSrc} type="video/mp4" /> : null}
            </video>

            {(videoError || !videoSrc) && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-slate-950/90 p-6">
                <div className="max-w-sm text-center text-white">
                  <PlayCircle className="mx-auto mb-4 h-10 w-10 text-indigo-400" />
                  <h3 className="text-lg font-semibold">Onboarding-Video vorbereiten</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-300">
                    Hinterlege dein Video unter <code className="rounded bg-white/10 px-1.5 py-0.5">/public/assets/onboarding/welcome-video.mp4</code> oder
                    setze <code className="rounded bg-white/10 px-1.5 py-0.5">REACT_APP_ONBOARDING_VIDEO_URL</code>.
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-1 flex-col justify-between gap-8 bg-white p-6 sm:p-10">
            <div className="space-y-8">
              <div>
                <Badge variant="outline" className="mb-4 border-gray-200 bg-gray-50 font-medium text-gray-700">
                  {currentPlanConfig.badge} Plan
                </Badge>
                <h2 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">Willkommen bei SportyPost</h2>
                <p className="mt-3 text-base leading-relaxed text-gray-500">
                  Nach den Willkommensgrüßen unserer beiden Gründer führen wir dich Schritt für Schritt zu deinem ersten Post.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-50 border border-gray-100">
                    <Sparkles className="h-4 w-4 text-gray-600" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">Template einmal anlegen, immer wieder nutzen</h4>
                    <p className="mt-1 text-sm leading-relaxed text-gray-500">
                      Du baust dein Design nur einmal auf. Danach befüllst du dasselbe Template immer wieder mit aktuellen Inhalten aus dem Spielalltag.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-50 border border-gray-100">
                    <MousePointerClick className="h-4 w-4 text-gray-600" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">Mit wenigen Klicks zum fertigen Post</h4>
                    <p className="mt-1 text-sm leading-relaxed text-gray-500">
                      Wähle Spiele, Spieler, Sponsoren und weitere Inhalte aus und die Plattform setzt sie automatisch in dein Template ein.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-50 border border-gray-100">
                    <CheckCircle2 className="h-4 w-4 text-gray-600" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">Auf mehreren Plattformen posten oder planen</h4>
                    <p className="mt-1 text-sm leading-relaxed text-gray-500">
                      Veröffentliche Beiträge direkt auf mehreren Kanälen gleichzeitig oder plane sie für den passenden Zeitpunkt vor.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-auto flex items-center justify-between gap-4 border-t border-gray-100 pt-6">
              <Button variant="ghost" onClick={onSkip} className="px-0 text-gray-500 hover:text-gray-900 sm:px-4">
                Video überspringen
              </Button>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-500">
                  {videoEnded ? "Abgeschlossen" : "Video läuft..."}
                </span>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export type { OnboardingVideoDialogProps };
